import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicHeader, PublicFooter } from "@/components/PublicHeader";
import { Button } from "@/components/ui/button";
import { Check, Gift, Building2 } from "lucide-react";

const PACK_META = {
  p100: { tagline: "For quick experiments", highlight: false },
  p200: { tagline: "For side projects", highlight: false },
  p500: { tagline: "For serious builders", highlight: true },
  p1000: { tagline: "For power users", highlight: false },
  corporate: { tagline: "For teams & companies", highlight: false },
};

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    api.get("/payments/packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  const go = () => navigate(user ? "/billing" : "/login");

  const standard = packs.filter((p) => p.id !== "corporate");
  const corporate = packs.find((p) => p.id === "corporate");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <PublicHeader />

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[#00FF66] text-sm font-mono mb-3">// simple credit-based pricing</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter">
            Pay only for what you build.
          </h1>
          <p className="text-zinc-400 mt-4">
            Every new account gets <span className="text-[#00FF66] font-semibold">50 free credits</span>.
            Top up anytime — credits never expire.
          </p>
        </div>

        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-[#141415] text-sm text-zinc-300 mt-10 mx-auto flex w-fit">
          <Gift className="w-4 h-4 text-[#00FF66]" />
          Credit cost per message: Gemini Pro = 1 · GPT-5.4 = 2 · Claude Sonnet = 3
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {standard.map((p) => {
            const meta = PACK_META[p.id] || {};
            return (
              <div
                key={p.id}
                data-testid={`pricing-card-${p.id}`}
                className={`relative flex flex-col p-6 rounded-2xl border transition-colors ${
                  meta.highlight
                    ? "border-[#00FF66] bg-[#101a12]"
                    : "border-white/10 bg-[#141415] hover:border-white/20"
                }`}
              >
                {meta.highlight && (
                  <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#00FF66] text-black text-xs font-semibold">
                    Most popular
                  </span>
                )}
                <p className="text-sm text-zinc-500">{meta.tagline}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-heading font-extrabold text-4xl tracking-tighter text-white">₹{p.amount}</span>
                </div>
                <p className="text-[#00FF66] font-mono text-sm mt-2">{p.credits.toLocaleString()} credits</p>
                <div className="h-px bg-white/10 my-5" />
                <ul className="space-y-2.5 text-sm text-zinc-400 flex-1">
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#00FF66] shrink-0 mt-0.5" /> All 3 AI models</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#00FF66] shrink-0 mt-0.5" /> Image uploads</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#00FF66] shrink-0 mt-0.5" /> Credits never expire</li>
                </ul>
                <Button
                  data-testid={`pricing-buy-${p.id}`}
                  onClick={go}
                  className={`mt-6 h-11 rounded-lg font-semibold transition-colors ${
                    meta.highlight
                      ? "bg-[#00FF66] hover:bg-[#00CC52] text-black"
                      : "bg-[#1F1F22] hover:bg-[#2a2a2e] text-white"
                  }`}
                >
                  Get {p.credits} credits
                </Button>
              </div>
            );
          })}
        </div>

        {corporate && (
          <div
            data-testid="pricing-card-corporate"
            className="mt-6 rounded-2xl border border-white/10 bg-gradient-to-r from-[#141415] to-[#101a12] p-8 flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-[#00FF66]/10 flex items-center justify-center shrink-0">
                <Building2 className="w-7 h-7 text-[#00FF66]" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-2xl text-white">Corporate</h3>
                <p className="text-zinc-400 mt-1">
                  {corporate.credits.toLocaleString()} credits for entire teams —{" "}
                  <span className="text-[#00FF66] font-semibold">₹{corporate.amount.toLocaleString()}</span> only
                </p>
              </div>
            </div>
            <Button
              data-testid="pricing-buy-corporate"
              onClick={go}
              className="h-12 px-8 bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold rounded-xl transition-colors"
            >
              Get {corporate.credits.toLocaleString()} credits
            </Button>
          </div>
        )}
      </section>

      <PublicFooter />
    </div>
  );
}
